import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function StatusModal({ onClose }) {
    const [employeeId, setEmployeeId] = useState('');
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);

    const handleSearch = async (e) => {
        e.preventDefault();
        
        // Trim the input to remove spaces
        const trimmedId = employeeId.trim();
        
        if (!trimmedId) {
            setError('Please enter your Employee ID');
            return;
        }

        setLoading(true);
        setError('');
        setSearched(true);
        setRequest(null);

        try {
            // Use the trimmed ID in the API call
            const response = await axios.get(`${API_URL}/api/status/${trimmedId}`);
            
            console.log('API Response:', response.data); // Debug log
            
            if (response.data && response.data.id) {
                setRequest(response.data);
            } else {
                setError('No requests found for this Employee ID');
            }
        } catch (error) {
            console.error('Error fetching request:', error);
            
            // Handle specific error cases
            if (error.response) {
                // The request was made and the server responded with a status code
                if (error.response.status === 404) {
                    setError('No requests found for this Employee ID');
                } else if (error.response.status === 400) {
                    setError('Invalid Employee ID format');
                } else if (error.response.status === 500) {
                    setError('Server error. Please try again later.');
                } else {
                    setError(`Error: ${error.response.status} - ${error.response.data?.error || 'Unknown error'}`);
                }
            } else if (error.request) {
                // The request was made but no response was received
                setError('Network error. Please check your connection.');
            } else {
                // Something happened in setting up the request
                setError('Failed to fetch request. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'approved':
                return <span className="status-badge approved"><i className="fas fa-check-circle"></i> Approved</span>;
            case 'rejected':
                return <span className="status-badge rejected"><i className="fas fa-times-circle"></i> Rejected</span>;
            case 'pending':
            default:
                return <span className="status-badge pending"><i className="fas fa-clock"></i> Pending</span>;
        }
    };

    const getRequestTypeIcon = (type) => type === 'Swap' ? 'fa-exchange-alt' : 'fa-calendar-day';
    const getRequestTypeClass = (type) => type === 'Swap' ? 'type-swap' : 'type-off';

    // Helper to format dates
    const formatDates = (dateString) => {
        if (!dateString) return 'N/A';
        if (dateString.includes(',')) {
            return dateString.split(',').map(d => 
                new Date(d).toLocaleDateString("en-US", { 
                    month: "short", 
                    day: "numeric", 
                    year: "numeric" 
                })
            ).join(' | ');
        }
        return new Date(dateString).toLocaleDateString("en-US", { 
            weekday: "short", 
            month: "short", 
            day: "numeric", 
            year: "numeric" 
        });
    };

    return (
        <div className="status-modal-overlay" onClick={onClose}>
            <div className="status-modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>✕</button>
                
                <h3><i className="fas fa-search"></i> Check Status</h3>
                
                <form onSubmit={handleSearch} className="status-search-form">
                    <div className="form-group">
                        <label><i className="fas fa-id-card"></i> Employee ID</label>
                        <input
                            type="text"
                            value={employeeId}
                            onChange={(e) => setEmployeeId(e.target.value)}
                            placeholder="Enter your Employee ID"
                            className="status-input"
                        />
                    </div>
                    <button type="submit" className="search-btn" disabled={loading}>
                        {loading ? (
                            <><i className="fas fa-spinner fa-spin"></i> Searching...</>
                        ) : (
                            <><i className="fas fa-search"></i> Check Status</>
                        )}
                    </button>
                </form>

                {error && (
                    <div className="status-error">
                        <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                )}

                {searched && !loading && request && request.id && (
                    <div className="status-results">
                        <h4><i className="fas fa-ticket-alt"></i> Latest Request Status</h4>
                        <div className="status-card-minimal">
                            <div className="card-header-row">
                                <div className="card-left-badges">
                                    <div className={`request-type-badge ${getRequestTypeClass(request.request_type)}`}>
                                        <i className={`fas ${getRequestTypeIcon(request.request_type)}`}></i> {request.request_type}
                                    </div>
                                    {getStatusBadge(request.status)}
                                </div>
                                <span className="request-id"><i className="fas fa-hashtag"></i> {request.id}</span>
                            </div>
                            
                            <div className="card-details-grid">
                                <div className="detail-item">
                                    <span className="detail-label"><i className="fas fa-calendar-alt"></i> Date(s)</span>
                                    <span className="detail-value">{formatDates(request.request_date)}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label"><i className="fas fa-user"></i> Name</span>
                                    <span className="detail-value">{request.employee_name}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label"><i className="fas fa-id-card"></i> Employee ID</span>
                                    <span className="detail-value">{request.employee_id}</span>
                                </div>
                                {request.duty_code && (
                                    <div className="detail-item">
                                        <span className="detail-label"><i className="fas fa-code"></i> Duty Code</span>
                                        <span className="detail-value">{request.duty_code}</span>
                                    </div>
                                )}
                                {request.swap_with && (
                                    <div className="detail-item">
                                        <span className="detail-label"><i className="fas fa-exchange-alt"></i> Swap With</span>
                                        <span className="detail-value">{request.swap_with}</span>
                                    </div>
                                )}
                                {/* Admin Note Display */}
                                {request.admin_note && (
                                    <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                                        <span className="detail-label"><i className="fas fa-sticky-note"></i> Admin Note</span>
                                        <span className="detail-value" style={{ color: '#ab6272', fontStyle: 'italic' }}>
                                            "{request.admin_note}"
                                        </span>
                                    </div>
                                )}
                            </div>
                            
                            {request.remarks && (
                                <div className="card-remarks">
                                    <i className="fas fa-comment"></i>
                                    <span>{request.remarks}</span>
                                </div>
                            )}
                            
                            <div className="card-meta">
                                <i className="fas fa-paperclip"></i>
                                {new Date(request.created_at).toLocaleString()}
                                {request.updated_at && request.updated_at !== request.created_at && (
                                    <span> • <i className="fas fa-edit"></i> {new Date(request.updated_at).toLocaleString()}</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {searched && !loading && !request && !error && (
                    <div className="no-results">
                        <p><i className="fas fa-exclamation-circle"></i> No requests found for <strong>{employeeId.trim()}</strong></p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default StatusModal;